/**
 * Which video container a media URL points at, for the transparent-WebM (VP9 alpha) path of
 * video planes: video hotspots, video portals and stacked-marker video layers. Shared with the
 * editor (src/components), which shows the "WebM transparency" option from the same answer.
 *
 * Real URLs rarely END in `.webm`:
 * - Firebase Storage download URLs keep the object path percent-encoded in one segment and add a
 *   query: `https://firebasestorage.googleapis.com/v0/b/<bucket>/o/users%2Fu%2Fsign.webm?alt=media&token=…`
 * - the CDN / storage-proxy rewrite keeps that encoded segment:
 *   `https://<proxy>/<bucket>/users%2Fu%2Fsign.webm?alt=media&ss_scene=…`
 * - Tauri (LOCAL_MODE) asset URLs percent-encode a whole file path (`/` or `\`):
 *   `asset://localhost/%2FUsers%2Fme%2F…%2Fsign.webm`, `http://asset.localhost/C%3A%5C…%5Csign.webm`
 * - editor-local `blob:` URLs are opaque; createNamedBlobUrl puts the file name in the fragment
 *   (`blob:…#sign.webm`), otherwise the stored upload name (`videoName`, a layer's `name`) is the
 *   only clue
 * - `data:video/webm;base64,…`, and self-hosted export paths (`assets/sign.webm`).
 *
 * The answer is three-way: 'webm', 'other' (a recognised non-WebM format, e.g. MP4: never alpha)
 * or 'unknown' (the URL and hint cannot tell; the creator's explicit `webmHasAlpha: true` decides).
 */
export type VideoFormatGuess = 'webm' | 'other' | 'unknown';

/** Extensions of formats that are definitely not WebM (videos and images a media field might hold). */
const NON_WEBM_EXTENSIONS: ReadonlySet<string> = new Set([
    'mp4', 'm4v', 'mov', 'qt', 'ogv', 'ogg', 'avi', 'wmv', 'flv', '3gp', '3g2', 'mpg', 'mpeg', 'm2ts', 'mts', 'ts',
    'm3u8', 'mpd', 'hevc', 'h264', 'h265',
    'gif', 'png', 'apng', 'jpg', 'jpeg', 'webp', 'avif', 'bmp', 'svg',
]);
/** Query parameters some hosts use to name the served format (`?format=webm`, `?fm=webm`). */
const FORMAT_QUERY_KEYS: ReadonlySet<string> = new Set(['format', 'fm', 'ext', 'extension', 'filetype']);
/** A media MIME type (no dots, so a relative path such as `video/sign.webm` is not mistaken for one). */
const MIME_PATTERN = /^(video|audio|image)\/[a-z0-9][a-z0-9+_-]*$/;

/** Percent-decode to a fixed point (Firebase / Tauri paths can be encoded more than once); never throws. */
function decodeLoose(value: string): string {
    let decoded = value;
    for (let pass = 0; pass < 4; pass++) {
        let next: string;
        try {
            // A literal '%' that is not an escape (`50% off.webm`) would make decodeURIComponent throw.
            next = decodeURIComponent(decoded.replace(/%(?![0-9a-fA-F]{2})/g, '%25'));
        } catch {
            return decoded;
        }
        if (next === decoded) return decoded;
        decoded = next;
    }
    return decoded;
}

function classifyExtension(ext: string | null): VideoFormatGuess {
    if (!ext) return 'unknown';
    if (ext === 'webm') return 'webm';
    return NON_WEBM_EXTENSIONS.has(ext) ? 'other' : 'unknown';
}

/** Extension of the last path segment of an already-decoded path or file name (`/` and `\` both separate). */
function extensionOf(decodedPath: string): string | null {
    const base = decodedPath.trim().split(/[\\/]/).pop() ?? '';
    const match = /\.([a-z0-9]{1,8})$/i.exec(base.trim());
    return match ? match[1].toLowerCase() : null;
}

function classifyMime(mime: string): VideoFormatGuess {
    const type = mime.trim().toLowerCase().split(';')[0].trim();
    if (type === 'video/webm') return 'webm';
    if (type.startsWith('video/') || type.startsWith('image/')) return 'other';
    return 'unknown';
}

function classifyQuery(query: string): VideoFormatGuess {
    if (!query) return 'unknown';
    for (const pair of query.split('&')) {
        const eq = pair.indexOf('=');
        if (eq <= 0) continue;
        const key = decodeLoose(pair.slice(0, eq).replace(/\+/g, ' ')).trim().toLowerCase();
        const value = decodeLoose(pair.slice(eq + 1).replace(/\+/g, ' ')).trim().toLowerCase();
        if (!value) continue;
        // `response-content-type=video/webm` (signed GCS / S3 URLs), `contentType=video/mp4`, …
        if (MIME_PATTERN.test(value.split(';')[0].trim())) {
            const guess = classifyMime(value);
            if (guess !== 'unknown') return guess;
            continue;
        }
        if (FORMAT_QUERY_KEYS.has(key)) {
            const guess = classifyExtension(value.replace(/^\./, ''));
            if (guess !== 'unknown') return guess;
        }
    }
    return 'unknown';
}

/** A fragment that names a file (`#sign.webm`, namedBlobUrl), not a media fragment (`#t=10`). */
function classifyFragment(fragment: string): VideoFormatGuess {
    if (!fragment || fragment.includes('=')) return 'unknown';
    return classifyExtension(extensionOf(decodeLoose(fragment)));
}

function classifyUrlOnly(rawUrl: string): VideoFormatGuess {
    const url = rawUrl.trim();
    if (!url) return 'unknown';
    if (/^data:/i.test(url)) {
        const comma = url.indexOf(',');
        const meta = url.slice(5, comma >= 0 ? comma : undefined);
        return classifyMime(meta);
    }
    const hashIndex = url.indexOf('#');
    const fragment = hashIndex >= 0 ? url.slice(hashIndex + 1) : '';
    const beforeHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
    const queryIndex = beforeHash.indexOf('?');
    // Scheme and authority go first, so a host name (`https://cdn.example.mp4.host`) is never read as a file.
    const path = (queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash).replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, '');
    const query = queryIndex >= 0 ? beforeHash.slice(queryIndex + 1) : '';
    // A blob: URL's path is an opaque UUID; only its fragment can name the file.
    if (!/^blob:/i.test(url)) {
        // Decoding the whole path first exposes Firebase's encoded `users%2F…%2Fsign.webm` and
        // Tauri's encoded file paths as ordinary segments.
        const fromPath = classifyExtension(extensionOf(decodeLoose(path)));
        if (fromPath !== 'unknown') return fromPath;
        const fromQuery = classifyQuery(query);
        if (fromQuery !== 'unknown') return fromQuery;
    }
    return classifyFragment(fragment);
}

type NameHint = string | null | undefined;

/**
 * Container of the video at `url`. The URL decides when it can; otherwise each name hint (an
 * upload's original file name, such as a hotspot's `videoName` or a stack layer's `name`) is
 * tried in turn. Case-insensitive; never throws.
 */
export function classifyVideoFormat(url: string | null | undefined, nameHint?: NameHint | readonly NameHint[]): VideoFormatGuess {
    const fromUrl = typeof url === 'string' ? classifyUrlOnly(url) : 'unknown';
    if (fromUrl !== 'unknown') return fromUrl;
    const hints = Array.isArray(nameHint) ? nameHint : [nameHint as NameHint];
    for (const hint of hints) {
        if (typeof hint !== 'string') continue;
        const guess = classifyUrlOnly(hint);
        if (guess !== 'unknown') return guess;
    }
    return 'unknown';
}

/** True when the URL (or, failing that, a name hint) identifies a WebM file. */
export function isWebmUrl(url: string | null | undefined, nameHint?: NameHint | readonly NameHint[]): boolean {
    return classifyVideoFormat(url, nameHint) === 'webm';
}

export interface WebmAlphaSource {
    url: string | null | undefined;
    /** Original upload file name(s), used when the URL alone cannot tell (blob: URLs). */
    nameHint?: NameHint | readonly NameHint[];
    /** The creator's choice: false = never transparent, true = transparent even when the URL cannot tell. */
    webmHasAlpha?: boolean | null;
}

/**
 * Whether a video plane takes the WebM-alpha path (RGBA texture, texture alpha as opacity):
 * - `webmHasAlpha: false` always wins (the creator turned transparency off);
 * - a recognised WebM: on (the default for WebM);
 * - a recognised non-WebM (MP4, MOV, …): off, whatever the flag says, so an MP4 is never
 *   treated as an alpha WebM;
 * - an unrecognisable URL (opaque blob: URL without a name, an extensionless proxy URL): only
 *   when the creator set `webmHasAlpha: true`.
 */
export function resolveWebmAlpha(source: WebmAlphaSource): boolean {
    if (source.webmHasAlpha === false) return false;
    const format = classifyVideoFormat(source.url, source.nameHint);
    if (format === 'webm') return true;
    if (format === 'unknown') return source.webmHasAlpha === true;
    return false;
}

/**
 * The `webmHasAlpha` for a copy that drops the upload name (a duplicated marker keeps the URLs
 * but not the file names, which belong to the source's stored files). When the name was what
 * identified the URL's format (a LOCAL_MODE blob: URL), the copy gets the resolved answer as an
 * explicit flag so it renders like the source; otherwise the flag is returned unchanged.
 */
export function webmHasAlphaWithoutName(
    url: string | null | undefined,
    nameHint: NameHint | readonly NameHint[],
    webmHasAlpha: boolean | undefined,
): boolean | undefined {
    if (classifyVideoFormat(url) !== 'unknown') return webmHasAlpha;
    if (resolveWebmAlpha({ url, nameHint, webmHasAlpha })) return true;
    return webmHasAlpha === true ? false : webmHasAlpha;
}

/** Media fields of a video plane (video hotspot, video portal, stacked-marker video layer). */
export interface VideoPlaneAlphaSource {
    videoUrl: string;
    /** Original upload file name of `videoUrl`. */
    videoName?: string;
    webmHasAlpha?: boolean;
    useIOSVideoAlphaMethod?: boolean;
    forceIOSVideoAlphaMethodForAllDevices?: boolean;
    iosMainVideoUrl?: string;
    iosMainVideoName?: string;
    alphaMaskVideoUrl?: string;
}

export interface VideoPlaneAlphaPlan {
    /** The video the plane shows. */
    mainUrl: string;
    /** Grayscale alpha-mask video (the iOS dual-video method), or null. */
    alphaMaskUrl: string | null;
    /** The main video's own alpha channel is used (WebM / VP9 alpha). Never together with a mask. */
    webmAlpha: boolean;
}

/**
 * Which videos a video plane plays and where its transparency comes from. The iOS dual-video
 * method (main MP4 + grayscale mask, because iOS Safari cannot decode VP9 alpha) applies on iOS
 * when enabled, or on every device when forced; a mask takes precedence over WebM alpha.
 * Otherwise the main video's own alpha is used per resolveWebmAlpha.
 */
export function planVideoPlaneAlpha(source: VideoPlaneAlphaSource, isIOS: boolean): VideoPlaneAlphaPlan {
    const useIOSMethod = (isIOS && !!source.useIOSVideoAlphaMethod) || !!source.forceIOSVideoAlphaMethodForAllDevices;
    const useIOSMain = useIOSMethod && !!source.iosMainVideoUrl;
    const mainUrl = useIOSMain ? source.iosMainVideoUrl! : source.videoUrl;
    const alphaMaskUrl = useIOSMethod ? (source.alphaMaskVideoUrl || null) : null;
    const webmAlpha = !alphaMaskUrl && resolveWebmAlpha({
        url: mainUrl,
        nameHint: useIOSMain ? source.iosMainVideoName : source.videoName,
        webmHasAlpha: source.webmHasAlpha,
    });
    return { mainUrl, alphaMaskUrl, webmAlpha };
}

/** iPhone / iPod / iPad, including iPadOS 13+ Safari, which reports a desktop Macintosh UA. */
export function isIOSLikeDevice(nav: { userAgent?: string; platform?: string; maxTouchPoints?: number } | undefined =
    typeof navigator !== 'undefined' ? navigator : undefined): boolean {
    if (!nav) return false;
    return /iPad|iPhone|iPod/.test(nav.userAgent || '') || (nav.platform === 'MacIntel' && (nav.maxTouchPoints ?? 0) > 1);
}

/** Below this 8-bit alpha a sampled video pixel counts as transparent (VP9 alpha noise stays above). */
export const VIDEO_ALPHA_PROBE_THRESHOLD = 250;

/**
 * True when any pixel of RGBA image data (e.g. a video frame drawn into a small canvas) is
 * transparent or partly transparent. A WebM recognised as a possible alpha video often has no
 * alpha channel at all; only one that does is worth blending over the splats.
 */
export function imageDataHasAlpha(data: ArrayLike<number>, threshold: number = VIDEO_ALPHA_PROBE_THRESHOLD): boolean {
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] < threshold) return true;
    }
    return false;
}
